/**
 * SystemConsoleMenu — dropdown in the NDS header that surfaces every
 * internal admin surface. Anything not represented by a Capability tile
 * lives here so nothing gets orphaned when the OS shell is the primary
 * landing page.
 *
 *   • Click the pill → dropdown lists every admin route with an icon,
 *     label and one-line description.
 *   • Escape / click-outside closes it.
 *   • Non-blocking — floats over the header, no scrim.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowRightLeft, ChevronDown, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNdsEditor } from "@/lib/ndsEditor";
import { useOps } from "@/lib/opsCards";
import { SYSTEM_CONSOLE_ID, capAction, deleteConsoleItem, isSuperAdminUser, usePlacedItems } from "@/lib/placement";
import MoveToDialog from "@/components/nds/MoveToDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Case-insensitive super-admin allowlist. Must mirror `SUPER_ADMIN_EMAILS`
 *  in backend/roi.py — the API enforces the same gate server-side, so a
 *  regular partner who guesses `/admin/roi` still hits a 403. This is a
 *  UX hint, not the security boundary. */
const SUPER_ADMIN_EMAILS = new Set(["will.s@theexchangealliance.com", "will.s@theexchangealliance.org", "william@theexchangealliance.org"]);

const iconFor = (name) => LucideIcons[(name || "").split("-").map((x) => x[0]?.toUpperCase() + x.slice(1)).join("")] || LucideIcons.Grid3x3;

export default function SystemConsoleMenu() {
  const [open, setOpen] = useState(false);
  const [moving, setMoving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const boxRef = useRef(null);
  const { fireTileAction } = useNdsEditor();
  const ops = useOps();
  const placed = usePlacedItems();

  // Read the current user's email from AuthContext so we can hide
  // Super-Admin-only items from other partners.
  const { user } = useAuth();
  const email = (user?.email || "").trim().toLowerCase();
  const isSA = isSuperAdminUser(user);
  const isSuperAdmin = SUPER_ADMIN_EMAILS.has(email) || isSA;
  const items = useMemo(
    () => placed.filter((it) => it.vertical_id === SYSTEM_CONSOLE_ID && (!it.superAdminOnly || isSuperAdmin)),
    [placed, isSuperAdmin],
  );

  const confirmDelete = async () => {
    try {
      await deleteConsoleItem(deleting.id);
      await ops.reload();
      toast.success(`${deleting.label} removed from the System Console`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete item"); }
    setDeleting(null);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nds-system-console-toggle"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 hover:border-nex-gold/50 text-nex-chrome hover:text-nex-gold font-mono text-[0.7rem] tracking-[0.14em] md:tracking-[0.22em] uppercase transition-all duration-200"
      >
        <Settings2 className="w-3.5 h-3.5" /> System
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="nds-system-console-menu"
          className="fixed left-2 right-2 top-[72px] max-h-[75vh] overflow-y-auto md:max-h-none md:overflow-visible md:absolute md:left-auto md:right-0 md:top-full md:mt-2 md:w-[340px] bg-[#0B1220] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.65)] z-50"
        >
          <div className="px-4 py-3 border-b border-white/10">
            <div className="font-mono text-[0.6rem] tracking-[0.24em] text-nex-gold uppercase">
              // System Console
            </div>
            <div className="text-nex-silver font-semibold text-sm mt-0.5">All internal ops surfaces</div>
          </div>
          <ul className="max-h-[70vh] overflow-y-auto py-1">
            {items.map((it) => (
              <ConsoleRow key={it.id} it={it} isSA={isSA} onClose={() => setOpen(false)}
                onOpenCard={() => { setOpen(false); fireTileAction(capAction(it, ops.cards[it.id])); }}
                onMove={() => { setOpen(false); setMoving(it); }} onDelete={() => { setOpen(false); setDeleting(it); }} />
            ))}
            {items.length === 0 && <li className="px-4 py-3 text-xs text-nex-muted" data-testid="nds-system-console-empty">Nothing in the System Console.</li>}
          </ul>
        </div>
      )}
      {moving && <MoveToDialog item={moving} onClose={() => setMoving(null)} />}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="console-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.label}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes “{deleting?.label}” permanently from the System Console. It will not appear in any engine or the console.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="console-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="console-delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-500">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ConsoleRow({ it, isSA, onClose, onOpenCard, onMove, onDelete }) {
  const Icon = iconFor(it.icon);
  const slug = it.sys ? it.href.replace(/\W+/g, "-") : it.id;
  const body = (
    <>
      <div className={`w-8 h-8 grid place-items-center border shrink-0 ${it.superAdminOnly ? "border-red-500/30 bg-red-500/[0.05] text-red-400" : "border-white/10 bg-white/[0.03] text-nex-gold"}`}>
        <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 text-left">
        <div className="text-nex-silver font-medium text-sm leading-tight flex items-center gap-1.5">
          {it.label}
          {it.superAdminOnly && <span className="font-mono text-[0.5rem] tracking-[0.16em] uppercase text-red-400/80">Super</span>}
        </div>
        <div className="text-[0.7rem] text-nex-muted leading-tight mt-0.5 truncate">{it.description}</div>
      </div>
    </>
  );
  const cls = "flex-1 min-w-0 flex items-start gap-3 px-4 py-2.5 no-underline text-nex-silver";
  return (
    <li className="group flex items-center hover:bg-white/5 transition-colors" data-testid={`nds-system-row-${it.id}`}>
      {it.sys
        ? <Link to={it.href} onClick={onClose} data-testid={`nds-system-item-${slug}`} className={cls}>{body}</Link>
        : <button type="button" onClick={onOpenCard} data-testid={`nds-system-item-${slug}`} className={cls}>{body}</button>}
      {isSA && (
        <div className="flex items-center gap-1 pr-3 shrink-0">
          <button type="button" onClick={onMove} title="Move to engine" data-testid={`nds-system-move-${it.id}`}
            className="w-7 h-7 grid place-items-center border border-white/10 text-nex-muted hover:text-nex-gold hover:border-nex-gold/50 transition-colors">
            <ArrowRightLeft className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={onDelete} title="Delete from System Console" data-testid={`nds-system-delete-${it.id}`}
            className="w-7 h-7 grid place-items-center border border-white/10 text-nex-muted hover:text-red-400 hover:border-red-500/50 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </li>
  );
}
